import type { ComparisonResult } from '@/types/domain';

interface Props {
  result: ComparisonResult;
  selected?: boolean;
  onSelect?: () => void;
  interactive?: boolean;
}

const CONFIDENCE_LABEL: Record<ComparisonResult['confidence'], string> = {
  high: 'High confidence — all relevant measurements matched',
  medium: 'Medium confidence — some measurements were not published',
  low: 'Low confidence — limited overlapping data',
};

export function ResultCard({ result, selected, onSelect, interactive = true }: Props) {
  const Wrapper = interactive ? 'button' : 'div';

  return (
    <Wrapper
      {...(interactive
        ? {
            type: 'button',
            onClick: onSelect,
            'aria-pressed': selected,
          }
        : {})}
      className={`w-full text-left rounded-2xl p-4 shadow-card transition-colors ${
        selected ? 'border-2 border-brand bg-brand/5' : 'border border-stone-200 bg-white hover:border-brand/40'
      } ${interactive ? 'cursor-pointer' : ''}`}
    >
      {selected && (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-ink">
          <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3 shrink-0 fill-current">
            <path d="M13.7 3.7 6 11.4 2.3 7.7 3.7 6.3 6 8.6l6.3-6.3z" />
          </svg>
          Viewing fit map
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-ink">{result.targetBrandName}</p>
          <p className="text-2xl font-bold text-brand">{result.recommendedSize}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
            result.confidence === 'high'
              ? 'bg-ink text-white'
              : result.confidence === 'medium'
                ? 'bg-stone-200 text-ink'
                : 'bg-stone-100 text-muted'
          }`}
        >
          {result.confidence} confidence
        </span>
      </div>

      <p className="mt-3 text-sm text-ink/80">{result.fitDescription}</p>
      <p className="sr-only">{CONFIDENCE_LABEL[result.confidence]}</p>
    </Wrapper>
  );
}
